import { keepPreviousData, useQuery } from '@tanstack/react-query';

import { apiClient, type UserDto } from '@/api/client';
import type { UserPickerOption } from '@/components/ui/user-picker';

import { useDebouncedValue } from './useDebouncedValue';

export const USER_SEARCH_MIN_LENGTH = 1;
const USER_SEARCH_DEBOUNCE_MS = 200;
export const USER_SEARCH_DEFAULT_LIMIT = 25;

export interface UseUserSearchOptions {
  readonly limit?: number;
  /** Override the debounce window — tests pass `0` for determinism. */
  readonly debounceMs?: number;
}

export interface UseUserSearchResult {
  readonly results: readonly UserPickerOption[];
  readonly isLoading: boolean;
  readonly error: Error | null;
}

/**
 * Live tenant user search. Backed by `GET /api/v1/users/search`, which
 * substring-matches against name, full_name, email and is tenant-scoped
 * server-side. Caller supplies the raw query string; the hook debounces,
 * gates on a minimum length, and returns pre-mapped picker options.
 */
export function useUserSearch(
  rawQuery: string,
  options: UseUserSearchOptions = {},
): UseUserSearchResult {
  const limit = options.limit ?? USER_SEARCH_DEFAULT_LIMIT;
  const debounceMs = options.debounceMs ?? USER_SEARCH_DEBOUNCE_MS;

  const trimmed = rawQuery.trim();
  const debouncedQuery = useDebouncedValue(trimmed, debounceMs);
  const isActive = debouncedQuery.length >= USER_SEARCH_MIN_LENGTH;

  const query = useQuery<readonly UserPickerOption[]>({
    queryKey: ['search', 'users', debouncedQuery, limit] as const,
    queryFn: async ({ signal }) => {
      const response = await apiClient.searchUsers(
        { q: debouncedQuery, page: 0, size: limit },
        { signal },
      );
      return response.content.map(toPickerOption);
    },
    enabled: isActive,
    placeholderData: keepPreviousData,
    staleTime: 30_000,
  });

  const debounceSettling = trimmed !== debouncedQuery;
  const isLoading = isActive && (debounceSettling || query.isFetching);

  return {
    results: isActive ? (query.data ?? []) : [],
    isLoading,
    error: isActive ? (query.error ?? null) : null,
  };
}

function toPickerOption(user: UserDto): UserPickerOption {
  // Prefer fullName as the primary line; fall back to the username so the
  // option always has something visible. Email is shown as the subtitle.
  const primary =
    user.fullName !== undefined && user.fullName.trim().length > 0
      ? user.fullName
      : (user.name ?? user.email ?? '');
  return {
    guid: user.uuid ?? '',
    fullName: primary,
    subtitle: user.email ?? null,
  };
}
